'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, AlertTriangle, Users, CheckCircle, XCircle, Hand, ThumbsUp, Eye, AlertCircle } from 'lucide-react'

export default function BuddySystemPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-purple-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/safety"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          안전 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
            <div className="flex items-center gap-3 xxs:gap-2">
              <div className="p-2 xxs:p-1.5 rounded-lg bg-purple-500">
                <Users className="w-6 h-6 xxs:w-5 xxs:h-5 text-white" />
              </div>
              <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                버디 시스템의 중요성
              </h1>
            </div>
            <div className={`mt-3 xxs:mt-2 p-3 xxs:p-2 rounded-lg border-l-4 border-red-500 ${
              isDark ? 'bg-red-900/30' : 'bg-red-100'
            }`}>
              <p className={`text-sm xxs:text-xs font-bold ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                프리다이빙 제1원칙: 절대 혼자 다이빙하지 마세요!
              </p>
            </div>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 버디 시스템이란? */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                버디 시스템이란?
              </h2>
              <p className={`text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                버디 시스템은 두 명 이상의 다이버가 서로를 관찰하고 보호하는 안전 시스템입니다.
                한 명이 다이빙하는 동안 다른 한 명이 수면에서 지켜보는 방식입니다.
              </p>
            </div>

            {/* 버디의 역할 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                버디의 역할
              </h2>
              <div className="space-y-3 xxs:space-y-2">
                {[
                  {
                    title: '다이빙 전',
                    color: 'cyan',
                    items: ['서로의 컨디션 확인', '다이빙 계획 공유', '응급 신호 확인', '장비 체크']
                  },
                  {
                    title: '다이빙 중',
                    color: 'blue',
                    items: ['지속적인 관찰 (눈을 떼지 않음)', '시간 체크 (타이머 사용)', '이상 징후 감지', '즉시 구조 준비 자세']
                  },
                  {
                    title: '다이빙 후',
                    color: 'green',
                    items: ['상승 직후 상태 확인', 'OK 사인 교환', '충분한 휴식 시간 부여', '다음 다이빙 계획 논의']
                  },
                ].map((stage, idx) => {
                  const colorClasses = {
                    cyan: { bg: isDark ? 'bg-blue-900/20' : 'bg-blue-50', text: isDark ? 'text-blue-300' : 'text-blue-800' },
                    blue: { bg: isDark ? 'bg-blue-900/20' : 'bg-blue-50', text: isDark ? 'text-blue-300' : 'text-blue-800' },
                    green: { bg: isDark ? 'bg-green-900/20' : 'bg-green-50', text: isDark ? 'text-green-300' : 'text-green-800' },
                  }[stage.color]!
                  return (
                    <div key={idx} className={`p-4 xxs:p-3 rounded-xl ${colorClasses.bg}`}>
                      <h3 className={`font-bold text-base xxs:text-sm mb-2 ${colorClasses.text}`}>
                        {idx + 1}. {stage.title}
                      </h3>
                      <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {stage.items.map((item, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* 좋은 버디 vs 피해야 할 버디 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 xxs:gap-3">
              <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                <h3 className={`font-bold text-base xxs:text-sm mb-3 xxs:mb-2 ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                  좋은 버디의 조건
                </h3>
                <ul className={`space-y-2 xxs:space-y-1.5 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {['책임감과 신뢰성', '응급 대처 능력', '비슷한 실력 수준', '원활한 소통', '안전 우선 마인드'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 xxs:w-3.5 xxs:h-3.5 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
                <h3 className={`font-bold text-base xxs:text-sm mb-3 xxs:mb-2 ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                  피해야 할 버디
                </h3>
                <ul className={`space-y-2 xxs:space-y-1.5 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {['안전 수칙 무시', '과도한 경쟁심', '불성실한 관찰', '응급 대처 무지', '소통 불가'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 xxs:w-3.5 xxs:h-3.5 text-red-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 버디 신호 체계 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                버디 신호 체계
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { emoji: '👌', title: 'OK 신호', desc: '괜찮다, 준비됐다', color: 'green' },
                  { emoji: '👍', title: '상승 신호', desc: '올라오라, 좋다', color: 'blue' },
                  { emoji: '✋', title: '정지 신호', desc: '멈춰라, 기다려라', color: 'yellow' },
                  { emoji: '🚨', title: '긴급 신호', desc: '위험, 도와줘 (양팔 흔들기)', color: 'red', isAlert: true },
                ].map((signal, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 xxs:gap-2 p-3 xxs:p-2 rounded-lg ${
                      signal.isAlert
                        ? (isDark ? 'bg-red-900/30 border-l-4 border-red-500' : 'bg-red-100 border-l-4 border-red-500')
                        : (isDark ? 'bg-gray-700/50' : 'bg-white')
                    }`}
                  >
                    <span className="text-2xl xxs:text-xl">{signal.emoji}</span>
                    <div>
                      <p className={`font-semibold text-sm xxs:text-xs ${
                        signal.isAlert
                          ? (isDark ? 'text-red-300' : 'text-red-900')
                          : (isDark ? 'text-white' : 'text-gray-900')
                      }`}>
                        {signal.title}
                      </p>
                      <p className={`text-xs xxs:text-[10px] ${
                        signal.isAlert
                          ? (isDark ? 'text-red-200' : 'text-red-800')
                          : (isDark ? 'text-gray-400' : 'text-gray-500')
                      }`}>
                        {signal.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 버디 찾기 CTA */}
            <div className={`p-5 xxs:p-4 rounded-xl ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>
                버디 구하기 어려우신가요?
              </h3>
              <p className={`text-sm xxs:text-xs mb-3 xxs:mb-2 ${isDark ? 'text-purple-200/80' : 'text-purple-700'}`}>
                오늘다이빙에서 안전하고 신뢰할 수 있는 프리다이빙 버디를 찾아보세요!
              </p>
              <div className={`space-y-1.5 xxs:space-y-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-purple-500" />
                  <span>실력과 경험 수준으로 매칭</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-purple-500" />
                  <span>선호하는 풀장과 시간대 선택</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-purple-500" />
                  <span>사전 채팅으로 안전 수칙 확인</span>
                </div>
              </div>
              <Link
                href="/matching/new"
                className="inline-block px-5 xxs:px-4 py-2.5 xxs:py-2 text-sm xxs:text-xs font-medium bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                버디 매칭 신청하기
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
